import {
  CheckIcon,
  ChevronsUpDownIcon,
  GlobeIcon,
  LayersIcon,
  MonitorIcon,
  SearchIcon,
} from "lucide-react";
import { useMemo, useState, type KeyboardEvent, type ReactElement, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { EMPTY_WORLD_LIVENESS, type WorldLivenessRollups } from "~/neucleos/worldLiveness";
import {
  EMPTY_WORLD_UNSEEN_SETTLE,
  type WorldUnseenSettleRollups,
} from "~/neucleos/worldUnseenSettle";
import { enterAll, enterLocal, enterWorld } from "~/neucleos/worldStore";
import { worldDisplayName, type NeucleosWorldSummary } from "~/neucleos/worldSelection";
import {
  ROOT_SESSION_STATUS_LABELS,
  rootSessionStatusFillClass,
  type RootSessionStatus,
} from "~/rootSessionStrip";
import { DoorUnseenSettleMark } from "./DoorUnseenSettleMark";
import { WorldMark } from "./WorldMark";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverPopup, PopoverTrigger } from "./ui/popover";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

interface SwitcherOption {
  readonly key: string;
  readonly label: string;
  readonly group: "mode" | "stance" | "world";
  readonly selected: boolean;
  /** Most urgent live status behind this door; null renders no dot. */
  readonly status: RootSessionStatus | null;
  /** NV9: an unseen settle behind this door; never on the selected row. */
  readonly unseen: boolean;
  readonly enter: () => void;
}

/**
 * THE World selector (FD-CAN-11, principal supersession 2026-09-01: "lets
 * try no world tabs, roll local, all, then the worlds, into a modal with
 * marks, and some level of state" / "so move this to the right of neucleos
 * logo"): the flat door row is retired — this modal owns the whole
 * selection, seated beside the masthead, its trigger the current-stance
 * chip. Searchable, fully keyboard-driven, and it keeps O24/W42: Local is
 * a mode in its own labeled group, ALL is a stance of its own, never mixed
 * in among the Worlds. NV9's rows carry marks, liveness, and unseen state.
 */
export function WorldSwitcher({
  triggerContent,
  triggerClassName,
  worlds,
  currentWorldKey,
  localSelected,
  allSelected = false,
  brandMarkUrl = null,
  liveness = EMPTY_WORLD_LIVENESS,
  unseen = EMPTY_WORLD_UNSEEN_SETTLE,
}: {
  readonly triggerContent?: ReactNode;
  readonly triggerClassName?: string;
  readonly worlds: ReadonlyArray<NeucleosWorldSummary>;
  readonly currentWorldKey: string | null;
  readonly localSelected: boolean;
  /** The ALL stance: the whole entitlement fronted, no World filter. */
  readonly allSelected?: boolean;
  /** The entered World's finalized mark (LG4), worn by the trigger chip. */
  readonly brandMarkUrl?: string | null;
  /** Live rollups so the overflow menu shows what the door row shows. */
  readonly liveness?: WorldLivenessRollups;
  /** NV9: the rail's one unseen-settle fold — presentation only, never computed here. */
  readonly unseen?: WorldUnseenSettleRollups;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const options = useMemo<ReadonlyArray<SwitcherOption>>(() => {
    const normalized = query.trim().toLocaleLowerCase();
    const local: SwitcherOption = {
      key: "local",
      label: "Local (ungoverned)",
      group: "mode",
      selected: localSelected,
      status: liveness.local,
      unseen: unseen.local && !localSelected,
      enter: enterLocal,
    };
    const allStance: SwitcherOption = {
      key: "all",
      label: "ALL Worlds",
      group: "stance",
      selected: allSelected,
      status: null,
      unseen: false,
      enter: enterAll,
    };
    const doors = worlds.map<SwitcherOption>((world) => {
      const selected = !localSelected && !allSelected && world.worldKey === currentWorldKey;
      return {
        key: world.worldKey,
        label: worldDisplayName(worlds, world.worldKey),
        group: "world",
        selected,
        status: liveness.byWorldKey.get(world.worldKey) ?? null,
        unseen: !selected && unseen.byWorldKey.has(world.worldKey),
        enter: () => enterWorld(world.worldKey),
      };
    });
    const all = [local, allStance, ...doors];
    if (normalized.length === 0) return all;
    return all.filter((option) => option.label.toLocaleLowerCase().includes(normalized));
  }, [allSelected, currentWorldKey, liveness, localSelected, query, unseen, worlds]);

  const currentLabel = localSelected
    ? "Local (ungoverned)"
    : allSelected
      ? "ALL Worlds"
      : currentWorldKey === null
        ? null
        : worldDisplayName(worlds, currentWorldKey);

  const close = () => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
  };

  const choose = (option: SwitcherOption) => {
    close();
    if (!option.selected) option.enter();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (options.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % options.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + options.length) % options.length);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(options.length - 1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const option = options[activeIndex];
      if (option) choose(option);
    }
  };

  const groups: ReadonlyArray<{ readonly label: string; readonly options: SwitcherOption[] }> = [
    { label: "Mode", options: options.filter((option) => option.group === "mode") },
    { label: "Stance", options: options.filter((option) => option.group === "stance") },
    { label: "Worlds", options: options.filter((option) => option.group === "world") },
  ];

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setActiveIndex(
            Math.max(
              0,
              options.findIndex((option) => option.selected),
            ),
          );
          setOpen(true);
          return;
        }
        close();
      }}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              render={
                <Button
                  aria-label={
                    currentLabel === null
                      ? "Switch World"
                      : `Switch World — current: ${currentLabel}`
                  }
                  className={cn(
                    "h-6 min-w-0 max-w-full gap-1.5 rounded-full px-2 text-[11px] tracking-[0.08em] text-foreground hover:bg-accent",
                    triggerClassName,
                  )}
                  data-world-switcher=""
                  data-world-switcher-trigger={
                    localSelected ? "local" : allSelected ? "all" : (currentWorldKey ?? "none")
                  }
                  size="xs"
                  variant="ghost"
                />
              }
            >
              {/* FD-CAN-11 dated refinement 2026-09-01 ("take the mark out
                  of what's shown in row 1 … just have the test world put
                  the name"): the chip is NAME ONLY — the mark and the
                  complexity live in the modal below. */}
              {triggerContent ?? (
                <>
                  <span className="truncate">
                    {localSelected ? "Local" : allSelected ? "ALL" : (currentLabel ?? "Worlds")}
                  </span>
                  <ChevronsUpDownIcon
                    aria-hidden
                    className="size-3 shrink-0 text-muted-foreground"
                  />
                </>
              )}
            </PopoverTrigger>
          }
        />
        <TooltipPopup side="bottom">
          {currentLabel === null ? "Switch World" : `Switch World — current: ${currentLabel}`}
        </TooltipPopup>
      </Tooltip>
      <PopoverPopup align="end" className="w-64 p-1.5">
        <div className="mb-1 flex h-7 items-center gap-2 rounded-md px-2 text-xs text-muted-foreground">
          <SearchIcon aria-hidden className="size-3.5 shrink-0" />
          <Input
            autoFocus
            nativeInput
            unstyled
            aria-activedescendant={
              options[activeIndex] ? `world-switcher-option-${options[activeIndex].key}` : undefined
            }
            aria-autocomplete="list"
            aria-controls="world-switcher-options"
            aria-expanded={options.length > 0}
            aria-label="Search Worlds"
            className="min-w-0 flex-1 [&_[data-slot=input]]:h-auto [&_[data-slot=input]]:p-0 [&_[data-slot=input]]:text-xs [&_[data-slot=input]]:leading-normal [&_[data-slot=input]]:text-foreground [&_[data-slot=input]]:placeholder:text-muted-foreground"
            data-world-switcher-search=""
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search Worlds"
            role="combobox"
            type="search"
            value={query}
          />
        </div>
        {options.length === 0 ? (
          <p className="px-2 py-4 text-center text-xs text-muted-foreground" role="status">
            No Worlds match
          </p>
        ) : (
          <div
            aria-label="Operating modes"
            className="flex flex-col gap-0.5"
            id="world-switcher-options"
            role="listbox"
          >
            {groups.map((group) =>
              group.options.length === 0 ? null : (
                <div aria-label={group.label} key={group.label} role="group">
                  <p className="px-2 pb-0.5 pt-1 text-[10px] font-medium tracking-[0.16em] text-muted-foreground uppercase">
                    {group.label}
                  </p>
                  {group.options.map((option) => {
                    const index = options.indexOf(option);
                    const active = index === activeIndex;
                    return (
                      <button
                        aria-selected={option.selected}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs",
                          active
                            ? "bg-accent text-foreground"
                            : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                        )}
                        data-world-switcher-option={option.key}
                        data-world-switcher-option-unseen={option.unseen ? "" : undefined}
                        id={`world-switcher-option-${option.key}`}
                        key={option.key}
                        onClick={() => choose(option)}
                        onMouseMove={() => setActiveIndex(index)}
                        role="option"
                        tabIndex={-1}
                        type="button"
                      >
                        {option.group === "mode" ? (
                          <MonitorIcon aria-hidden className="size-3.5 shrink-0" />
                        ) : option.group === "stance" ? (
                          <LayersIcon aria-hidden className="size-3.5 shrink-0" />
                        ) : option.key === currentWorldKey ? (
                          // The entered World's row wears its real face —
                          // the mark lives here now, not on the chip.
                          <WorldMark
                            markUrl={brandMarkUrl}
                            name={option.label}
                            worldKey={option.key}
                          />
                        ) : (
                          <GlobeIcon aria-hidden className="size-3.5 shrink-0" />
                        )}
                        <span className="min-w-0 flex-1 truncate">{option.label}</span>
                        {option.status !== null ? (
                          <>
                            <span
                              aria-hidden="true"
                              className={`size-1.5 shrink-0 rounded-full forced-colors:bg-[ButtonText] ${rootSessionStatusFillClass(option.status)}`}
                            />
                            <span className="sr-only">
                              , {ROOT_SESSION_STATUS_LABELS[option.status]}
                            </span>
                          </>
                        ) : null}
                        <DoorUnseenSettleMark marked={option.unseen} />
                        {option.selected ? (
                          <CheckIcon aria-hidden className="size-3.5 shrink-0" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ),
            )}
          </div>
        )}
      </PopoverPopup>
    </Popover>
  );
}
